import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Request,
  RequestStatus,
  PaginatedResponse,
  DetailedGeneralRequest,
} from "../types/request";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { createGeneralRequestsApi } from "../api/generalRequestsApi";
import React, { useEffect, useMemo } from "react";

interface UseGeneralRequestsOptions {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
  status?: RequestStatus | RequestStatus[];
  category?: string;
  selectedFrame?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const useGeneralRequests = ({
  hotelId,
  initialPage = 1,
  initialLimit = 10,
  status,
  category,
  selectedFrame,
  sortBy,
  sortOrder = "desc",
}: UseGeneralRequestsOptions) => {
  const { callApi } = useAuthenticatedApi();
  const queryClient = useQueryClient();
  const { pagination, setPage, setLimit, setTotal } = usePagination({
    initialPage,
    initialLimit,
  });

  const [searchTerm, setSearchTerm] = React.useState<string>("");

  // Create API instance
  const api = useMemo(() => createGeneralRequestsApi(callApi), [callApi]);

  const apiStatusArray = React.useMemo(() => {
    if (!status) return undefined;
    return Array.isArray(status) ? status : [status];
  }, [status]);

  const queryKey = React.useMemo(() => {
    const baseKey = [
      "generalRequests",
      hotelId,
      pagination.page,
      pagination.limit,
      apiStatusArray ? [...apiStatusArray].sort().join(",") : undefined,
      category,
      selectedFrame,
      searchTerm,
      sortBy,
      sortOrder,
    ];
    return baseKey.filter((keyPart) => keyPart !== undefined);
  }, [
    hotelId,
    pagination.page,
    pagination.limit,
    apiStatusArray,
    category,
    selectedFrame,
    searchTerm,
    sortBy,
    sortOrder,
  ]);

  const {
    data: responseData,
    isLoading,
    error,
    refetch,
  } = useQuery<PaginatedResponse<DetailedGeneralRequest>, Error>({
    queryKey: queryKey,
    queryFn: async () => {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (apiStatusArray && apiStatusArray.length > 0) {
        params.status = apiStatusArray.join(",");
      }

      if (category) {
        params.category = category;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;

      return await api.fetchGeneralRequests(params);
    },
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (responseData?.meta?.totalCount !== undefined) {
      if (responseData.meta.totalCount !== pagination.total) {
        setTotal(responseData.meta.totalCount);
      }
    }
  }, [responseData?.meta?.totalCount, setTotal, pagination.total]);

  React.useEffect(() => {
    setPage(1);
  }, [apiStatusArray, category, selectedFrame, setPage]);

  const updateStatusMutation = useMutation<
    Request,
    Error,
    { requestId: string; status: RequestStatus; hotelId: string }
  >({
    mutationFn: async (params) => {
      const response = await api.updateGeneralRequestStatus(params.requestId, {
        status: params.status,
      });
      return response as any;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["generalRequests"] });
    },
  });

  const fetchRequestById = React.useCallback(
    async (requestId: string) => {
      return await api.fetchGeneralRequestById(requestId);
    },
    [api]
  );

  const processedRequests = React.useMemo(() => {
    if (!responseData?.data) return [];
    return responseData.data;
  }, [responseData?.data]);

  return {
    requests: processedRequests,
    isLoading,
    error,
    updateRequestStatus: updateStatusMutation.mutateAsync,
    fetchRequestById,
    pagination,
    setPage,
    setLimit,
    setSearchTerm,
    refetch,
  };
};
